import { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { OrderDetail } from '../../lib/orders';
import {
  createReparatie,
  listReparateurs,
  type CreateReparatieResponse,
  type ReparateurDto,
} from '../../lib/reparatie';

const INTERN_PREFIX =
  'INTERNE REPARATIE, schade door ons veroorzaakt. Deze gaat niet naar de klant en de klant betaalt niets. Graag alleen je prijs en verwachte doorlooptijd aan ons doorgeven, dan plannen wij de rit.';

interface Props {
  order: OrderDetail;
  onClose: () => void;
  onCreated: () => void;
  prefillSelectedWiId?: string | null;
}

export function ReparatieAanvraagDialog({ order, onClose, onCreated, prefillSelectedWiId }: Props) {
  const [reparateurs, setReparateurs] = useState<ReparateurDto[]>([]);
  const [reparateurId, setReparateurId] = useState('');
  const [loadingReparateurs, setLoadingReparateurs] = useState(true);
  const [opdrachtAlgemeen, setOpdrachtAlgemeen] = useState('');
  const [perItemTekst, setPerItemTekst] = useState<Record<string, string>>({});
  const [prefilledIds, setPrefilledIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateReparatieResponse | null>(null);
  const [isIntern, setIsIntern] = useState(false);
  // Offerte-vooraf: de reparateur heeft al geprijsd (bv. op klantfoto's vóór
  // de ophaal). Dan geen aanvraagmail en direct 'Offerte ontvangen'.
  const [offerteVooraf, setOfferteVooraf] = useState(false);
  const [inkoop, setInkoop] = useState('');
  const [marge, setMarge] = useState('50');

  // De reparateur moet weten dat dit niet naar een klant gaat, anders wacht hij
  // op een klant-akkoord dat nooit komt (REP-0002383 stond zo 14 dagen stil).
  // De tekst is bewerkbaar; aanvinken zet hem alleen als er nog niets staat en
  // uitvinken haalt hem alleen weg als hij onveranderd is.
  const toggleIntern = (checked: boolean) => {
    setIsIntern(checked);
    setOpdrachtAlgemeen((huidig) => {
      if (checked) return huidig.trim() === '' ? INTERN_PREFIX : huidig;
      return huidig === INTERN_PREFIX ? '' : huidig;
    });
  };

  // Filter: alleen primaire WI's met dimensies (= vloerkleden, niet upsells)
  const kleden = order.items
    .map((g) => g.primary)
    .filter((p) => p.areaM2 != null || p.lengthCm != null || p.diameterCm != null);

  // Pre-fill: kleden met een klantverzoek krijgen de tekst alvast in
  // perItemTekst — kantoor hoeft dan niet opnieuw te typen.
  useEffect(() => {
    const initial: Record<string, string> = {};
    const prefilled = new Set<string>();
    for (const k of kleden) {
      if (k.klantverzoekReparatie && k.klantverzoekReparatie.trim().length > 0) {
        initial[k.id] = k.klantverzoekReparatie;
        prefilled.add(k.id);
      }
    }
    if (Object.keys(initial).length > 0) {
      setPerItemTekst((prev) => ({ ...initial, ...prev }));
      setPrefilledIds(prefilled);
    }
    if (prefillSelectedWiId && kleden.some((k) => k.id === prefillSelectedWiId)) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.add(prefillSelectedWiId);
        return next;
      });
    }
    // Run once on mount; orderId-changes worden door de parent als nieuw
    // mount afgehandeld omdat de dialog dan opnieuw opent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingReparateurs(true);
    listReparateurs()
      .then((list) => {
        if (cancelled) return;
        setReparateurs(list);
        setLoadingReparateurs(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoadingReparateurs(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleItem = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setItemTekst = (id: string, tekst: string) => {
    setPerItemTekst((prev) => ({ ...prev, [id]: tekst }));
    if (prefilledIds.has(id)) {
      setPrefilledIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const inkoopNum = parseFloat(inkoop.replace(',', '.'));
  const margeNum = isIntern ? 0 : parseFloat(marge.replace(',', '.'));
  const offerteVoorafGeldig =
    !offerteVooraf || (Number.isFinite(inkoopNum) && inkoopNum > 0 && Number.isFinite(margeNum) && margeNum >= 0);
  const verkoopPreview =
    offerteVooraf && offerteVoorafGeldig ? Math.round(inkoopNum * (1 + margeNum / 100) * 100) / 100 : null;

  const canSubmit =
    !submitting &&
    !!reparateurId &&
    selected.size > 0 &&
    offerteVoorafGeldig &&
    Array.from(selected).every((id) => ((perItemTekst[id] ?? '') as string).trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const items = Array.from(selected).map((wiId) => ({
        wiId,
        opdrachtTekst: ((perItemTekst[wiId] ?? '') as string).trim(),
      }));
      const res = await createReparatie({
        workOrderId: order.id,
        reparateurAccountId: reparateurId,
        opdrachtAlgemeen: opdrachtAlgemeen.trim(),
        items,
        betaler: isIntern ? 'intern' : 'klant',
        offerteVooraf: offerteVooraf ? { inkoopBedrag: inkoopNum, margePercentage: margeNum } : null,
      });
      if (!res.success) {
        setError(res.message || 'Aanvraag mislukt');
        setSubmitting(false);
        return;
      }
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aanvraag mislukt');
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onCreated();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Reparatie aanvragen</h2>
          <p className="text-xs text-slate-500 mt-1">
            {order.orderNumber}
            {order.customer?.name ? ` — ${order.customer.name}` : ''}
          </p>
        </div>

        {result ? (
          <div className="px-6 py-6 space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-sm font-medium text-emerald-900">
                Aanvraag {result.opdrachtNummer} aangemaakt
              </p>
              <p className="text-xs text-emerald-700 mt-1">
                {result.mailSkipped
                  ? `Offerte vooraf vastgelegd (status ${result.status ?? 'Offerte ontvangen'}${
                      result.verkoopBedrag != null ? `, klantprijs € ${result.verkoopBedrag.toFixed(2).replace('.', ',')}` : ''
                    }). Geen aanvraagmail naar de reparateur. Volgende stap: voorstel naar de klant, of het akkoord vastleggen als de klant al ja zei.`
                  : result.mailSent
                    ? 'Mail naar reparateur verstuurd.'
                    : `Mail-verzending mislukt: ${result.mailError || 'onbekende fout'}. Gebruik "Verstuur opnieuw" in de lijst.`}
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleDone}
                className="text-sm font-medium px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Sluiten
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4">
              <div
                className={clsx(
                  'rounded border px-3 py-2.5',
                  isIntern ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-slate-50/60'
                )}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isIntern}
                    onChange={(e) => toggleIntern(e.target.checked)}
                    className="h-4 w-4 mt-0.5"
                    disabled={submitting}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      Interne reparatie — wij betalen
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Eigen schade, coulance of garantie. De klant krijgt geen offerte, betaalt
                      niets en hoort hier niets over. Marge staat op 0.
                    </p>
                  </div>
                </label>
                {isIntern && (
                  <p className="text-xs text-violet-800 mt-2 pl-7">
                    Let op: de klant weet nog van niets. Bel of mail zelf over de vertraging.
                  </p>
                )}
              </div>

              <div
                className={clsx(
                  'rounded border px-3 py-2.5',
                  offerteVooraf ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50/60'
                )}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={offerteVooraf}
                    onChange={(e) => setOfferteVooraf(e.target.checked)}
                    className="h-4 w-4 mt-0.5"
                    disabled={submitting}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">Offerte al ontvangen</div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      De reparateur heeft al een prijs gegeven (bijvoorbeeld op foto's van de klant, vóór de
                      ophaal). Er gaat geen aanvraagmail uit en de opdracht start direct op 'Offerte ontvangen'.
                    </p>
                  </div>
                </label>
                {offerteVooraf && (
                  <div className="mt-3 pl-7 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Inkoop (excl. marge)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                        placeholder="bijv. 80"
                        value={inkoop}
                        onChange={(e) => setInkoop(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Marge %</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 disabled:bg-slate-100"
                        value={isIntern ? '0' : marge}
                        onChange={(e) => setMarge(e.target.value)}
                        disabled={submitting || isIntern}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-600">
                      {verkoopPreview != null
                        ? `Klantprijs: € ${verkoopPreview.toFixed(2).replace('.', ',')} incl. btw`
                        : 'Vul een inkoopbedrag in.'}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Reparateur</label>
                <select
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  value={reparateurId}
                  onChange={(e) => setReparateurId(e.target.value)}
                  disabled={loadingReparateurs || submitting}
                >
                  <option value="">— Kies een reparateur —</option>
                  {reparateurs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.naam}
                      {r.email ? ` · ${r.email}` : ' · (geen e-mail)'}
                    </option>
                  ))}
                </select>
                {!loadingReparateurs && reparateurs.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    Geen reparateurs gevonden. Markeer eerst een Account met Is_Reparateur__c=true.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Algemene context (optioneel)
                </label>
                <textarea
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  rows={3}
                  placeholder="Bijv. klant heeft haast — graag binnen 2 weken terug."
                  value={opdrachtAlgemeen}
                  onChange={(e) => setOpdrachtAlgemeen(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">
                  Selecteer kleden + omschrijf de reparatie
                </p>
                {kleden.length === 0 && (
                  <p className="text-sm text-slate-500">Geen kleden gevonden op deze order.</p>
                )}
                <div className="space-y-2">
                  {kleden.map((k) => {
                    const isSel = selected.has(k.id);
                    return (
                      <div
                        key={k.id}
                        className={clsx(
                          'border rounded p-3 space-y-2',
                          isSel ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200'
                        )}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleItem(k.id)}
                            className="h-4 w-4 mt-0.5"
                            disabled={submitting}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{k.name || 'Kleed'}</div>
                            <div className="text-xs text-slate-500">
                              {k.itemNumber}
                              {k.areaM2 != null ? ` · ${k.areaM2.toFixed(2)} m²` : ''}
                            </div>
                          </div>
                        </label>
                        {isSel && (
                          <div className="space-y-1">
                            {prefilledIds.has(k.id) && (
                              <p className="text-[11px] text-amber-700">
                                💡 Overgenomen uit klantverzoek (kun je aanpassen)
                              </p>
                            )}
                            <textarea
                              className={clsx(
                                'w-full text-sm border rounded px-2 py-1.5',
                                prefilledIds.has(k.id)
                                  ? 'border-amber-300 bg-amber-50/40'
                                  : 'border-slate-300'
                              )}
                              rows={2}
                              placeholder="Wat moet er gedaan worden? Bijv. randen aan beide korte zijden vastlijmen."
                              value={perItemTekst[k.id] || ''}
                              onChange={(e) => setItemTekst(k.id, e.target.value)}
                              disabled={submitting}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>

            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  canSubmit
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                )}
              >
                {submitting ? 'Versturen…' : 'Aanvraag versturen'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
